import React from "react";
import { format } from "date-fns";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { AuditLogChangeType, AuditLogDiff } from "@common/types/auditLog";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { CHANGE_TYPE_VERB } from "../../utils/getActivityDescription";
import { DiffCell } from "../journalEntries/cells/DiffCell";
import {
    COUNTERPARTY_FIELD_SECTIONS,
    CounterpartyFieldDef,
    CounterpartyVersion,
} from "./views/historyCounterpartyDetailsViewConfig";
import styles from "./HistoryCounterpartyDetails.module.scss";

function getDiff(
    diffs: AuditLogDiff[],
    field: string,
): AuditLogDiff | undefined {
    return diffs.find((d) => d.field === field);
}

function formatValue(value: unknown): string | null {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    return String(value);
}

interface CounterpartyFieldRowProps {
    fieldDef: CounterpartyFieldDef;
    diff: AuditLogDiff | undefined;
    changeType: AuditLogChangeType;
}

const CounterpartyFieldRow: React.FC<CounterpartyFieldRowProps> = ({
    fieldDef,
    diff,
    changeType,
}) => {
    if (!diff) {
        return null;
    }

    const { label, presentValue } = fieldDef;
    const present = presentValue ?? ((value: string | null) => value);
    const oldVal = present(
        formatValue(diff.before.valueLabel ?? diff.before.value),
    );
    const newVal = present(
        formatValue(diff.after.valueLabel ?? diff.after.value),
    );

    const isCreate = changeType === AuditLogChangeType.CREATE;
    const isDelete = changeType === AuditLogChangeType.DELETE;
    const hasChanges = oldVal !== newVal;

    if (isCreate && !newVal) {
        return null;
    }
    if (isDelete && !oldVal) {
        return null;
    }
    if (!isCreate && !isDelete && !hasChanges) {
        return null;
    }

    return (
        <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{label}</span>
            <div className={styles.fieldValue}>
                <DiffCell
                    oldValue={oldVal}
                    newValue={isDelete ? oldVal : newVal}
                    hasChanges={hasChanges && !isCreate && !isDelete}
                    isAdded={isCreate}
                    isRemoved={isDelete}
                />
            </div>
        </div>
    );
};

interface FieldSectionProps {
    title: string;
    fields: CounterpartyFieldDef[];
    diffs: AuditLogDiff[];
    changeType: AuditLogChangeType;
}

const FieldSection: React.FC<FieldSectionProps> = ({
    title,
    fields,
    diffs,
    changeType,
}) => {
    const hasAnyDiff = fields.some((f) => getDiff(diffs, f.field));
    if (!hasAnyDiff) {
        return null;
    }

    return (
        <div className={styles.section}>
            <h4 className={styles.sectionTitle}>{title}</h4>
            <div className={styles.fields}>
                {fields.map((f) => (
                    <CounterpartyFieldRow
                        key={f.field}
                        fieldDef={f}
                        diff={getDiff(diffs, f.field)}
                        changeType={changeType}
                    />
                ))}
            </div>
        </div>
    );
};

interface HistoryCounterpartyDetailsProps {
    versions: CounterpartyVersion[];
    footer: React.ReactNode;
}

export const HistoryCounterpartyDetails: React.FC<
    HistoryCounterpartyDetailsProps
> = ({ versions, footer }) => {
    if (versions.length === 0) {
        return <EmptyState header="No counterparty changes available" />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.versions}>
                {versions.map((version) => (
                    <div key={version.versionId} className={styles.version}>
                        <div className={styles.versionHeader}>
                            <span className={styles.versionLabel}>
                                {CHANGE_TYPE_VERB[version.changeType]}
                            </span>
                            <span className={styles.versionName}>
                                {version.counterpartyName}
                            </span>
                            <span className={styles.versionTime}>
                                {format(
                                    new Date(version.logDate),
                                    fullDateFormatWithTime,
                                )}
                            </span>
                        </div>
                        <div className={styles.versionBody}>
                            {COUNTERPARTY_FIELD_SECTIONS.map((section) => (
                                <FieldSection
                                    key={section.title}
                                    title={section.title}
                                    fields={section.fields}
                                    diffs={version.diffs}
                                    changeType={version.changeType}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {footer}
        </div>
    );
};
